import type { ReactNode } from 'react';
import { InfoIcon } from '@src/components/icons/InfoIcon';
import { WarningIcon } from '@src/components/icons/WarningIcon';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Note.css';

/**
 * success — a step is done and here is what to do next.
 * info — explains the screen.
 * warning — a consequence worth reading before acting.
 * neutral — a quiet aside.
 */
export type NoteTone = 'success' | 'info' | 'warning' | 'neutral';

export interface NoteProps {
  children: ReactNode;
  tone?: NoteTone;
  /** Replaces the tone's default glyph. Pass `null` for no icon at all. */
  icon?: ReactNode | null;
  /**
   * center — centres the icon on the copy block (default).
   * start — lines the icon up with the first line when copy runs long.
   */
  iconAlign?: 'start' | 'center';
  testId?: string;
}

/**
 * A block of explanation attached to a section — v1's Tip. Distinct from Hint,
 * which belongs to a form control and speaks as an alert.
 */
export function Note({
  children,
  tone = 'info',
  icon,
  iconAlign = 'center',
  testId,
}: NoteProps) {
  const glyph = icon === undefined ? (tone === 'warning' ? <WarningIcon /> : <InfoIcon />) : icon;

  return (
    <div
      className={classes('note', `note--${tone}`, iconAlign === 'start' && 'note--icon-start')}
      {...testIdAttr(testId)}
    >
      {glyph ? (
        <span className="note__icon" aria-hidden="true">
          {glyph}
        </span>
      ) : null}
      <Typography as="div" role="body/sm" color="inherit" className="note__copy">
        {children}
      </Typography>
    </div>
  );
}
